import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Activity,
    ArrowRight,
    BarChart3,
    Bot,
    Check,
    ChevronDown,
    Clock3,
    Globe2,
    Menu,
    ShieldCheck,
    SlidersHorizontal,
    Sparkles,
    Target,
    TrendingUp,
    X,
    Zap,
} from 'lucide-react';
import SEO from '../SEO';
import { PRICING_MATRIX, TIERS, VOLUME_STEPS, formatPrice, formatVolume, type TierId } from '../../constants/pricing';
import { getBrand } from '../../utils/brand';
import { PUBLIC_LANDING_PATHS } from '../../routes/publicLandingPaths';
import type { LandingPageConfig, TemplateTranslations } from './TrafficLandingTemplate';

interface TrafficLandingV2Props {
    config: LandingPageConfig;
    translations: TemplateTranslations;
}

interface SmartActionProps {
    href: string;
    className: string;
    children: React.ReactNode;
    onClick?: () => void;
}

const SmartAction: React.FC<SmartActionProps> = ({ href, className, children, onClick }) => {
    if (href.startsWith('http://') || href.startsWith('https://')) {
        return (
            <a href={href} className={className} target="_blank" rel="noreferrer" onClick={onClick}>
                {children}
            </a>
        );
    }

    if (href.startsWith('#')) {
        return <a href={href} className={className} onClick={onClick}>{children}</a>;
    }

    return <Link to={href} className={className} onClick={onClick}>{children}</Link>;
};

const LANGUAGE_CODES = new Set([
    'ar', 'bn', 'cs', 'de', 'es', 'fr', 'he', 'hi', 'id', 'it', 'ja', 'ko',
    'nl', 'pl', 'pt', 'ro', 'ru', 'sv', 'th', 'tl', 'tr', 'vi', 'zh',
]);

const RTL_LANGUAGES = new Set(['ar', 'he']);
const DISPLAY_VOLUMES = [VOLUME_STEPS[0], VOLUME_STEPS[1], VOLUME_STEPS[2], VOLUME_STEPS[3]] as const;

const routeLanguage = (pathname: string): string => {
    const candidate = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    return candidate && LANGUAGE_CODES.has(candidate) ? candidate : 'en';
};

const visibleOfferSchema = (config: LandingPageConfig, canonicalUrl: string) => {
    if (config.seoPricing) {
        return config.seoPricing.packages.map((pkg) => ({
            '@type': 'Offer',
            name: `${pkg.credits.toLocaleString('en-US')} ${config.seoPricing?.unitLabel ?? 'credits'}`,
            price: (pkg.credits * pkg.price).toFixed(2),
            priceCurrency: 'USD',
            url: `${canonicalUrl}#pricing`,
            eligibleQuantity: {
                '@type': 'QuantitativeValue',
                value: pkg.credits + pkg.free,
                unitText: config.seoPricing?.unitLabel ?? 'credits',
            },
        }));
    }

    if (config.resellerPricing) {
        return config.resellerPricing.tiers.flatMap((tier) => {
            const numericPrice = Number(tier.monthlyPrice.replace(/[^0-9.]/g, ''));
            if (!Number.isFinite(numericPrice) || numericPrice <= 0) return [];
            return [{
                '@type': 'Offer',
                name: `${tier.name} monthly plan`,
                price: numericPrice.toFixed(2),
                priceCurrency: tier.monthlyPrice.includes('€') ? 'EUR' : 'USD',
                url: tier.ctaHref || `${canonicalUrl}#pricing`,
            }];
        });
    }

    return DISPLAY_VOLUMES.map((volume) => ({
        '@type': 'Offer',
        name: `Professional ${formatVolume(volume)} Hit Pack`,
        price: PRICING_MATRIX.professional[volume][1].toFixed(2),
        priceCurrency: 'USD',
        url: `${canonicalUrl}#pricing`,
        eligibleQuantity: {
            '@type': 'QuantitativeValue',
            value: volume,
            unitText: 'hits',
        },
    }));
};

const highlightedHeadline = (headline: string, keyword: string): React.ReactNode => {
    const matchIndex = headline.toLocaleLowerCase().indexOf(keyword.toLocaleLowerCase());
    if (matchIndex < 0) return headline;

    return (
        <>
            {headline.slice(0, matchIndex)}
            <span>{headline.slice(matchIndex, matchIndex + keyword.length)}</span>
            {headline.slice(matchIndex + keyword.length)}
        </>
    );
};

const Logo: React.FC = () => (
    <span className="tcv2-logo" aria-label="Traffic Creator">
        <span className="tcv2-logo-mark" aria-hidden="true"><Bot size={22} /></span>
        <span className="tcv2-logo-copy"><strong>TRAFFIC</strong> <em>CREATOR</em></span>
    </span>
);

const SectionHeading: React.FC<{
    eyebrow: string;
    title: string;
    copy?: string;
}> = ({ eyebrow, title, copy }) => (
    <div className="tcv2-section-heading">
        <div className="tcv2-eyebrow"><Sparkles size={15} /> {eyebrow}</div>
        <h2>{title}</h2>
        {copy && <p>{copy}</p>}
    </div>
);

const TrafficLandingV2: React.FC<TrafficLandingV2Props> = ({ config, translations: t }) => {
    const location = useLocation();
    const brand = getBrand();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const [selectedTier, setSelectedTier] = useState<TierId>('professional');

    const canonicalPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');
    const canonicalUrl = `${brand.website}${canonicalPath}`;
    const lang = routeLanguage(canonicalPath);
    const direction = RTL_LANGUAGES.has(lang) ? 'rtl' : 'ltr';

    useEffect(() => {
        const root = document.documentElement;
        const previousLang = root.lang;
        const previousDir = root.dir;
        root.lang = lang;
        root.dir = direction;

        return () => {
            root.lang = previousLang;
            root.dir = previousDir;
        };
    }, [direction, lang]);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [canonicalPath]);

    const schema = useMemo(() => ([
        {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            name: config.productName,
            description: config.productDescription,
            url: canonicalUrl,
            isPartOf: { '@id': `${brand.website}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#service` },
            // Freshness signal. Without it the page dates itself by whatever the
            // crawler last saw, and answer engines weight recency.
            ...(config.lastUpdated ? { dateModified: config.lastUpdated } : {}),
            publisher: { '@id': `${brand.website}/#organization` },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'Service',
            '@id': `${canonicalUrl}#service`,
            name: config.productName,
            description: config.productDescription,
            serviceType: config.primaryKeyword,
            url: canonicalUrl,
            provider: {
                '@type': 'Organization',
                name: brand.name,
                url: brand.website,
            },
            hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: `${config.productName} offers`,
                itemListElement: visibleOfferSchema(config, canonicalUrl),
            },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: config.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        },
        {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: brand.website },
                { '@type': 'ListItem', position: 2, name: config.primaryKeyword, item: canonicalUrl },
            ],
        },
    ]), [brand.name, brand.website, canonicalUrl, config]);

    const heroPrimaryHref = config.heroPrimaryHref ?? '/signup';
    const heroSecondaryHref = config.heroSecondaryHref ?? '#pricing';
    const bottomPrimaryHref = config.ctaBottomPrimaryHref ?? '/signup';
    const bottomSecondaryHref = config.ctaBottomSecondaryHref ?? '#pricing';
    const selectedTierDefinition = TIERS.find((tier) => tier.id === selectedTier) ?? TIERS[0];

    return (
        <div className="tcv2" data-landing-design="redesign" dir={direction} lang={lang}>
            <SEO
                title={config.titleTag}
                description={config.metaDescription}
                keywords={config.keywords}
                schema={schema}
            />

            <a className="tcv2-skip" href="#tcv2-main">Skip to main content</a>

            <header className="tcv2-header">
                <div className="tcv2-shell tcv2-header-inner">
                    <Link to="/" className="tcv2-brand-link"><Logo /></Link>
                    <nav className="tcv2-nav" aria-label="Primary navigation">
                        <a href="#features">{t.navFeatures}</a>
                        <a href="#pricing">{t.navPricing}</a>
                        <a href="#faq">{t.navFaq}</a>
                        <Link to="/blog">Blog</Link>
                    </nav>
                    <div className="tcv2-header-actions">
                        <Link to="/login" className="tcv2-login">{t.navLogin}</Link>
                        <SmartAction href={heroPrimaryHref} className="tcv2-button tcv2-button-primary tcv2-header-cta">
                            {t.heroCtaPrimary} <ArrowRight size={17} />
                        </SmartAction>
                        <button
                            type="button"
                            className="tcv2-menu-button"
                            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                            aria-expanded={mobileMenuOpen}
                            aria-controls="tcv2-mobile-navigation"
                            onClick={() => setMobileMenuOpen((current) => !current)}
                        >
                            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
                <nav
                    id="tcv2-mobile-navigation"
                    className="tcv2-mobile-nav"
                    aria-label="Mobile navigation"
                    hidden={!mobileMenuOpen}
                >
                    <a href="#features" onClick={() => setMobileMenuOpen(false)}>{t.navFeatures}</a>
                    <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t.navPricing}</a>
                    <a href="#faq" onClick={() => setMobileMenuOpen(false)}>{t.navFaq}</a>
                    <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>{t.navLogin}</Link>
                    <SmartAction
                        href={heroPrimaryHref}
                        className="tcv2-button tcv2-button-primary tcv2-mobile-primary"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        {t.heroCtaPrimary} <ArrowRight size={17} />
                    </SmartAction>
                </nav>
            </header>

            <main id="tcv2-main">
                <section className="tcv2-hero">
                    <div className="tcv2-dot-field tcv2-dot-field-hero" aria-hidden="true" />
                    <div className="tcv2-shell tcv2-hero-grid">
                        <div className="tcv2-hero-copy">
                            <div className="tcv2-eyebrow"><Zap size={15} /> {t.heroBadge}</div>
                            <h1>{highlightedHeadline(config.h1, config.primaryKeyword)}</h1>
                            <p className="tcv2-hero-lede">{config.subheadline}</p>
                            <div className="tcv2-hero-actions">
                                <SmartAction href={heroPrimaryHref} className="tcv2-button tcv2-button-primary">
                                    {t.heroCtaPrimary} <ArrowRight size={18} />
                                </SmartAction>
                                <SmartAction href={heroSecondaryHref} className="tcv2-button tcv2-button-secondary">
                                    {t.heroCtaSecondary}
                                </SmartAction>
                            </div>
                            <p className="tcv2-microcopy"><ShieldCheck size={16} /> {t.heroMicrocopy}</p>
                            <div className="tcv2-capabilities" aria-label="Campaign capabilities">
                                {config.solutionPoints.slice(0, 3).map((point) => (
                                    <span key={point}><Check size={15} /> {point}</span>
                                ))}
                            </div>
                        </div>

                        <figure className="tcv2-dashboard">
                            <div className="tcv2-browser-bar" aria-hidden="true">
                                <span className="tcv2-browser-dots"><i /><i /><i /></span>
                                <span className="tcv2-browser-url"><ShieldCheck size={12} /> traffic-creator.com/dashboard</span>
                                <span className="tcv2-browser-live"><i /> {t.heroLiveLabel}</span>
                            </div>
                            <div className="tcv2-dashboard-media">
                                <img
                                    src="/helpdesk/media/dashboard/current-dashboard-overview.webp?v=20260716-landing-mockup"
                                    alt="Traffic Creator demo dashboard with active campaigns and 30-day delivery analytics"
                                    width="1732"
                                    height="908"
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                />
                            </div>
                            <figcaption><Activity size={16} /> Real product interface · Demo workspace</figcaption>
                        </figure>
                    </div>
                    <div className="tcv2-wave" aria-hidden="true" />
                </section>

                <section className="tcv2-trust-strip" aria-label="Platform characteristics">
                    <div className="tcv2-shell">
                        {[
                            [Globe2, t.statsCountries, 'Coverage'],
                            [BarChart3, 'GA4-visible', 'Analytics'],
                            [SlidersHorizontal, 'Campaign controls', 'Flexible'],
                            [Clock3, t.statsSupport, 'Support'],
                        ].map(([Icon, label, value]) => {
                            /*
                             * Konkreter Typ statt `React.ElementType`: seit
                             * `@react-three/fiber` im Projekt ist (Beams-Hero des
                             * Template-Kits, `template/sections/hero/HeroBeams.tsx`),
                             * traegt die globale JSX-Liste rund hundert three-Elemente.
                             * Die gemeinsame Schnittmenge von `ElementType` faellt damit
                             * auf `never` und `size` ist nicht mehr zuweisbar. Hier steht
                             * ohnehin immer ein Lucide-Symbol.
                             */
                            const IconComponent = Icon as React.ComponentType<{ size?: number }>;
                            return (
                                <div className="tcv2-trust-item" key={String(label)}>
                                    <IconComponent size={20} />
                                    <span><strong>{String(value)}</strong><small>{String(label)}</small></span>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* === AEO: answer-first block ===
                    Answer engines retrieve passages, not pages. This is the first
                    prose after the hero so the definition and the direct answer are
                    what gets chunked, rather than the marketing headline. */}
                {(config.quickAnswer || config.explainer) && (
                    <section className="tcv2-section tcv2-answer" id="quick-answer">
                        <div className="tcv2-section-shell">
                            <div className="tcv2-answer-body">
                                {config.quickAnswer && (
                                    <>
                                        <h2 className="tcv2-answer-h2">{config.quickAnswer.h2}</h2>
                                        <p className="tcv2-answer-lead">{config.quickAnswer.answer}</p>
                                        {config.quickAnswer.facts && config.quickAnswer.facts.length > 0 && (
                                            <dl className="tcv2-answer-facts">
                                                {config.quickAnswer.facts.map((fact) => (
                                                    <div key={fact.label}>
                                                        <dt>{fact.label}</dt>
                                                        <dd>{fact.value}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}
                                    </>
                                )}
                                {config.explainer && (
                                    <div className="tcv2-answer-explainer">
                                        <h2 className="tcv2-answer-h2">{config.explainer.h2}</h2>
                                        {config.explainer.paragraphs.map((para, index) => (
                                            <p key={index}>{para}</p>
                                        ))}
                                    </div>
                                )}
                                {config.lastUpdated && (
                                    <p className="tcv2-answer-updated">
                                        Last updated: <time dateTime={config.lastUpdated}>{config.lastUpdated}</time>
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                <section className="tcv2-section tcv2-problem">
                    <div className="tcv2-dot-field tcv2-dot-field-left" aria-hidden="true" />
                    <div className="tcv2-section-shell">
                        <SectionHeading eyebrow={config.secondaryKeyword} title={config.problemH2} copy={config.problemText} />
                        <div className="tcv2-contrast-grid">
                            <article className="tcv2-contrast-card">
                                <span className="tcv2-card-label">{t.problemWithout} {brand.name}</span>
                                <ul>{t.problemBullets.map((point) => <li key={point}><X size={17} /> {point}</li>)}</ul>
                            </article>
                            <article className="tcv2-contrast-card tcv2-contrast-card-accent">
                                <span className="tcv2-card-label">{t.problemWith} {brand.name}</span>
                                <ul>{config.solutionPoints.map((point) => <li key={point}><Check size={17} /> {point}</li>)}</ul>
                            </article>
                        </div>
                    </div>
                </section>

                <section className="tcv2-section tcv2-features" id="features">
                    <div className="tcv2-section-shell">
                        <SectionHeading eyebrow={t.navFeatures} title={config.featuresH2} copy={t.featuresSubtitle} />
                        <div className="tcv2-feature-grid">
                            {config.features.map((feature) => (
                                <article className="tcv2-feature-card" key={feature.title}>
                                    <div className="tcv2-feature-icon">{feature.icon}</div>
                                    <h3>{feature.title}</h3>
                                    <p>{feature.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="tcv2-section tcv2-workflow">
                    <div className="tcv2-section-shell">
                        <SectionHeading eyebrow={t.howItWorksH2} title={`${brand.name} · ${t.howItWorksH2}`} />
                        <div className="tcv2-workflow-grid">
                            {t.howItWorksSteps.map((step, index) => (
                                <article className="tcv2-workflow-card" key={`${step.title}-${index}`}>
                                    <span>{index + 1}</span>
                                    <div>{index === 0 ? <Target /> : index === 1 ? <Zap /> : <TrendingUp />}</div>
                                    <h3>{step.title}</h3>
                                    <p>{step.desc.replace('{keyword}', config.primaryKeyword)}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="tcv2-section tcv2-pricing" id="pricing">
                    <div className="tcv2-dot-field tcv2-dot-field-right" aria-hidden="true" />
                    <div className="tcv2-section-shell">
                        {config.resellerPricing ? (
                            <>
                                <SectionHeading
                                    eyebrow={t.navPricing}
                                    title={config.resellerPricing.h2 ?? t.pricingH2}
                                    copy={config.resellerPricing.subtitle ?? t.pricingSubtitle}
                                />
                                <div className="tcv2-pricing-grid tcv2-pricing-grid-reseller">
                                    {config.resellerPricing.tiers.map((tier) => (
                                        <article className={`tcv2-price-card${tier.highlighted ? ' tcv2-price-card-featured' : ''}`} key={tier.name}>
                                            {tier.highlighted && <div className="tcv2-popular"><Sparkles size={13} /> {t.pricingMostPopular}</div>}
                                            <span className="tcv2-plan-kicker">{tier.discount}</span>
                                            <h3>{tier.name}</h3>
                                            <div className="tcv2-price"><strong>{tier.monthlyPrice}</strong><span>/ month</span></div>
                                            <p className="tcv2-plan-detail">{tier.setupFee} · {tier.subAccounts}</p>
                                            <ul>{tier.features.map((feature) => <li key={feature}><Check size={16} /> {feature}</li>)}</ul>
                                            <SmartAction href={tier.ctaHref ?? '/signup'} className="tcv2-button tcv2-button-primary tcv2-plan-button">
                                                {tier.ctaLabel ?? t.pricingGetStarted} <ArrowRight size={16} />
                                            </SmartAction>
                                        </article>
                                    ))}
                                </div>
                            </>
                        ) : config.seoPricing ? (
                            <>
                                <SectionHeading
                                    eyebrow={t.navPricing}
                                    title={config.seoPricing.h2 ?? t.pricingH2}
                                    copy={config.seoPricing.subtitle ?? t.pricingSubtitle}
                                />
                                <div className="tcv2-pricing-grid tcv2-pricing-grid-seo">
                                    {config.seoPricing.packages.map((pkg, index) => {
                                        const total = pkg.credits * pkg.price;
                                        const checkoutUrl = `/dashboard/premium-checkout?type=${config.seoPricing?.type}&credits=${pkg.credits}&volume=${pkg.credits + pkg.free}&price=${total.toFixed(2)}`;
                                        return (
                                            <article className={`tcv2-price-card${index === 2 ? ' tcv2-price-card-featured' : ''}`} key={pkg.credits}>
                                                {pkg.discountLabel && <div className="tcv2-popular">{pkg.discountLabel}</div>}
                                                <span className="tcv2-plan-kicker">{config.seoPricing?.unitLabel ?? 'Credits'}</span>
                                                <h3>{pkg.credits.toLocaleString()} credits</h3>
                                                {pkg.free > 0 && <p className="tcv2-bonus">+{pkg.free.toLocaleString()} included</p>}
                                                <div className="tcv2-price"><strong>{formatPrice(total)}</strong><span>one time</span></div>
                                                <p className="tcv2-plan-detail">{formatPrice(pkg.price)} per credit</p>
                                                <SmartAction href={checkoutUrl} className="tcv2-button tcv2-button-primary tcv2-plan-button">
                                                    {t.pricingGetStarted} <ArrowRight size={16} />
                                                </SmartAction>
                                            </article>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <>
                                <SectionHeading eyebrow={t.navPricing} title={`${t.pricingH2} ${t.navPricing}`} copy={t.pricingSubtitle} />
                                <div className="tcv2-tier-tabs" role="group" aria-label="Traffic quality tier">
                                    {TIERS.map((tier) => (
                                        <button
                                            key={tier.id}
                                            type="button"
                                            aria-pressed={selectedTier === tier.id}
                                            className={selectedTier === tier.id ? 'is-active' : ''}
                                            onClick={() => setSelectedTier(tier.id)}
                                        >
                                            {tier.name}<small>{tier.quality}</small>
                                        </button>
                                    ))}
                                </div>
                                <div className="tcv2-pricing-grid">
                                    {DISPLAY_VOLUMES.map((volume, index) => {
                                        const price = PRICING_MATRIX[selectedTier][volume][1];
                                        return (
                                            <article className={`tcv2-price-card${index === 1 ? ' tcv2-price-card-featured' : ''}`} key={`${selectedTier}-${volume}`}>
                                                {index === 1 && <div className="tcv2-popular"><Sparkles size={13} /> {t.pricingMostPopular}</div>}
                                                <span className="tcv2-plan-kicker">{selectedTierDefinition.name}</span>
                                                <h3>{formatVolume(volume)} Hit Pack</h3>
                                                <div className="tcv2-price"><strong>{formatPrice(price)}</strong><span>one time</span></div>
                                                <p className="tcv2-plan-detail">{volume.toLocaleString()} {t.pricingVisitors}</p>
                                                <ul>
                                                    {selectedTierDefinition.features.map((feature) => <li key={feature}><Check size={16} /> {feature}</li>)}
                                                </ul>
                                                <Link to="/signup" className="tcv2-button tcv2-button-primary tcv2-plan-button">
                                                    {t.pricingGetStarted} <ArrowRight size={16} />
                                                </Link>
                                            </article>
                                        );
                                    })}
                                </div>
                                <div className="tcv2-assurances">
                                    <span><ShieldCheck /> {t.pricingGuarantee}</span>
                                    <span><Zap /> {t.pricingDelivery}</span>
                                    <span><Globe2 /> {t.pricingCountries}</span>
                                </div>
                            </>
                        )}
                    </div>
                </section>

                {config.testimonials.length > 0 && (
                    <section className="tcv2-section tcv2-proof">
                        <div className="tcv2-section-shell">
                            <SectionHeading eyebrow={t.socialProofLabel} title={config.testimonialsH2} />
                            <div className="tcv2-proof-grid">
                                {config.testimonials.map((testimonial) => (
                                    <article className="tcv2-proof-card" key={`${testimonial.name}-${testimonial.seed}`}>
                                        <div className="tcv2-quote">“</div>
                                        <p>{testimonial.text}</p>
                                        <div className="tcv2-proof-person">
                                            <span>{testimonial.name.charAt(0)}</span>
                                            <div><strong>{testimonial.name}</strong><small>{testimonial.role}</small></div>
                                        </div>
                                        <em>{testimonial.result}</em>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                <section className="tcv2-section tcv2-faq" id="faq">
                    <div className="tcv2-section-shell">
                        <SectionHeading eyebrow={t.navFaq} title={t.faqH2} />
                        <div className="tcv2-faq-list">
                            {config.faqs.map((faq, index) => {
                                const isOpen = openFaq === index;
                                return (
                                    <article className={`tcv2-faq-item${isOpen ? ' is-open' : ''}`} key={faq.question}>
                                        <button
                                            type="button"
                                            id={`tcv2-faq-button-${index}`}
                                            aria-expanded={isOpen}
                                            aria-controls={`tcv2-faq-panel-${index}`}
                                            onClick={() => setOpenFaq(isOpen ? null : index)}
                                        >
                                            <span>{faq.question}</span><ChevronDown size={20} />
                                        </button>
                                        <div
                                            id={`tcv2-faq-panel-${index}`}
                                            role="region"
                                            aria-labelledby={`tcv2-faq-button-${index}`}
                                            hidden={!isOpen}
                                        >
                                            <p>{faq.answer}</p>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="tcv2-final-cta">
                            <div className="tcv2-cta-icon"><TrendingUp size={31} /></div>
                            <div><h2>{config.ctaH2}</h2><p>{t.ctaSubtitle}</p></div>
                            <div className="tcv2-cta-actions">
                                <SmartAction href={bottomPrimaryHref} className="tcv2-button tcv2-button-primary">
                                    {t.ctaButtonPrimary} <ArrowRight size={17} />
                                </SmartAction>
                                <SmartAction href={bottomSecondaryHref} className="tcv2-button tcv2-button-secondary">
                                    {t.ctaButtonSecondary}
                                </SmartAction>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="tcv2-footer">
                <div className="tcv2-section-shell tcv2-footer-grid">
                    <div><Logo /><p>{config.primaryKeyword} {t.footerDescription}</p></div>
                    <div><h3>{t.footerRelatedServices}</h3>{config.relatedLinks.filter((link) => PUBLIC_LANDING_PATHS.has(link.to)).slice(0, 6).map((link) => <Link key={link.to} to={link.to}>{link.label}</Link>)}</div>
                    <div><h3>{t.footerCompany}</h3><Link to="/about">{t.footerAbout}</Link><Link to="/blog">{t.footerBlog}</Link><Link to="/helpdesk">{t.footerHelpCenter}</Link></div>
                    <div><h3>Legal</h3><Link to="/legal/privacy">{t.footerPrivacy}</Link><Link to="/legal/impressum">{t.footerImpressum}</Link><span className="tcv2-status"><i /> {t.footerStatus}</span></div>
                </div>
                <div className="tcv2-section-shell tcv2-footer-bottom">© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</div>
            </footer>
        </div>
    );
};

export default TrafficLandingV2;
